import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Building2, Filter, X, Edit, Trash2 } from 'lucide-react'
import { useCompanies, CompanyFilters } from '@/hooks/useCompanies'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { CompanyFormModal } from '@/components/company/CompanyFormModal'
import { DeleteCompanyModal } from '@/components/company/DeleteCompanyModal'
import { useToast } from '@/hooks/use-toast'
import type { Company } from '@/hooks/useCompanies'

export default function CompaniesList() {
  const navigate = useNavigate()
  const { toast } = useToast()

  // Get user from localStorage
  const userStr = localStorage.getItem('user')
  const currentUser = userStr ? JSON.parse(userStr) : null

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [revenueRanges, setRevenueRanges] = useState<string[]>([])
  const [aiStages, setAiStages] = useState<string[]>([])
  const [demoFilter, setDemoFilter] = useState<'all' | 'demo' | 'real'>('all')
  const [onboardingFilter, setOnboardingFilter] = useState<'all' | 'onboarded' | 'pending'>('all')
  const [showFilters, setShowFilters] = useState(false)

  // Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null)

  // Build filters object
  const filters: CompanyFilters = {
    search: searchQuery || undefined,
    revenueRange: revenueRanges.length > 0 ? revenueRanges : undefined,
    aiAdoptionStage: aiStages.length > 0 ? aiStages : undefined,
    isDemo: demoFilter === 'all' ? undefined : demoFilter === 'demo',
    isOnboarded: onboardingFilter === 'all' ? undefined : onboardingFilter === 'onboarded',
  }

  // Use API hook with filters
  const { companies, loading, error, refetchCompanies } = useCompanies(filters)

  // Check authentication and authorization
  if (!currentUser || currentUser.role !== 'super_admin') {
    navigate('/dashboard')
    return null
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('')
    setRevenueRanges([])
    setAiStages([])
    setDemoFilter('all')
    setOnboardingFilter('all')
  }

  // Handle successful company creation
  const handleCompanyCreated = () => {
    refetchCompanies()
    toast({
      title: 'Company created successfully',
      description: 'The new company has been added to the platform.',
    })
  }

  // Handle edit button click
  const handleEditClick = (company: Company, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedCompany(company)
    setIsEditModalOpen(true)
  }

  // Handle successful company update
  const handleCompanyUpdated = () => {
    refetchCompanies()
    toast({
      title: 'Company updated successfully',
      description: 'The company information has been updated.',
    })
  }

  // Handle delete button click
  const handleDeleteClick = (company: Company, e: React.MouseEvent) => {
    e.stopPropagation()
    setCompanyToDelete(company)
    setIsDeleteModalOpen(true)
  }

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch(`/api/admin/companies/${companyToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to delete company (${response.status})`)
      }

      setIsDeleteModalOpen(false)
      refetchCompanies()
      toast({
        title: 'Company deleted successfully',
        description: `The company "${companyToDelete.name}" and all associated users have been removed.`,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete company'
      toast({
        variant: 'destructive',
        title: 'Failed to delete company',
        description: errorMessage,
      })
      throw error // Re-throw to let DeleteCompanyModal handle loading state
    }
  }

  // Revenue range options
  const revenueRangeOptions = [
    '< 1M',
    '1M - 5M',
    '5M - 10M',
    '10M - 50M',
    '50M - 100M',
    '> 100M',
  ]

  // AI adoption stage options
  const aiAdoptionStageOptions = [
    'Not Started',
    'Exploring',
    'Pilot',
    'Implementing',
    'Mature',
  ]

  // Get badge color for AI adoption stage
  const getAiStageBadgeColor = (stage: string | null) => {
    switch (stage) {
      case 'Not Started':
        return 'bg-gray-500/10 text-gray-500 dark:bg-gray-400/10 dark:text-gray-400'
      case 'Exploring':
        return 'bg-blue-500/10 text-blue-500 dark:bg-blue-400/10 dark:text-blue-400'
      case 'Pilot':
        return 'bg-yellow-500/10 text-yellow-600 dark:bg-yellow-400/10 dark:text-yellow-400'
      case 'Implementing':
        return 'bg-orange-500/10 text-orange-600 dark:bg-orange-400/10 dark:text-orange-400'
      case 'Mature':
        return 'bg-green-500/10 text-green-600 dark:bg-green-400/10 dark:text-green-400'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Companies Management</h1>
            <p className="text-muted-foreground mt-1">
              View and manage all companies on the platform
            </p>
          </div>

          {/* Create Company Button */}
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Company
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive/50 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-destructive" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
              <button
                onClick={() => refetchCompanies()}
                className="ml-auto text-sm text-destructive hover:underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Search and Filter Toolbar */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-muted-foreground" />
            </div>
            <Input
              type="text"
              placeholder="Search companies by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filter Toggle and Active Filters */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="relative"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {(revenueRanges.length > 0 || aiStages.length > 0 || searchQuery || demoFilter !== 'all' || onboardingFilter !== 'all') && (
                <Badge className="ml-2 h-5 px-1.5 text-xs">
                  {revenueRanges.length + aiStages.length + (searchQuery ? 1 : 0) + (demoFilter !== 'all' ? 1 : 0) + (onboardingFilter !== 'all' ? 1 : 0)}
                </Badge>
              )}
            </Button>

            {/* Clear Filters Button */}
            {(revenueRanges.length > 0 || aiStages.length > 0 || searchQuery || demoFilter !== 'all' || onboardingFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Dropdowns */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-card border border-border rounded-lg">
              {/* Demo Status Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Demo Status
                </label>
                <Select
                  value={demoFilter}
                  onValueChange={(value: 'all' | 'demo' | 'real') => setDemoFilter(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    <SelectItem value="real">Real Companies Only</SelectItem>
                    <SelectItem value="demo">Demo Companies Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Onboarding Status Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Onboarding Status
                </label>
                <Select
                  value={onboardingFilter}
                  onValueChange={(value: 'all' | 'onboarded' | 'pending') => setOnboardingFilter(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="onboarded">Onboarding Complete</SelectItem>
                    <SelectItem value="pending">Pending Onboarding</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Revenue Range Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Revenue Range
                </label>
                <Select
                  value={revenueRanges[0] || ''}
                  onValueChange={(value) => setRevenueRanges(value ? [value] : [])}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select revenue range" />
                  </SelectTrigger>
                  <SelectContent>
                    {revenueRangeOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* AI Adoption Stage Filter */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  AI Adoption Stage
                </label>
                <Select
                  value={aiStages[0] || ''}
                  onValueChange={(value) => setAiStages(value ? [value] : [])}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select AI stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {aiAdoptionStageOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="min-h-[400px] flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading companies...</p>
            </div>
          </div>
        )}

        {/* Companies Table */}
        {!loading && companies.length === 0 && (
          <div className="bg-card rounded-lg shadow p-12 text-center">
            <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-medium text-foreground">No companies found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQuery || revenueRanges.length > 0 || aiStages.length > 0 || demoFilter !== 'all' || onboardingFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating a new company.'}
            </p>
          </div>
        )}

        {!loading && companies.length > 0 && (
          <>
            {/* Table View - Desktop */}
            <div className="hidden md:block bg-card rounded-lg shadow overflow-hidden border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Employees</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>AI Stage</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow
                      key={company.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/companies/${company.id}`)}
                    >
                      {/* Company Column */}
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          {company.logoUrl ? (
                            <img
                              src={company.logoUrl}
                              alt={company.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold">
                                {company.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-foreground truncate">
                                {company.name}
                              </p>
                              {/* Demo Badge */}
                              {company.isDemo && (
                                <Badge className="bg-purple-500/10 text-purple-600 dark:bg-purple-400/10 dark:text-purple-400">
                                  DEMO
                                </Badge>
                              )}
                              {/* Onboarding Status Badge */}
                              {company.isOnboarded ? (
                                <Badge className="bg-green-500/10 text-green-600 dark:bg-green-400/10 dark:text-green-400">
                                  Onboarding Complete
                                </Badge>
                              ) : company.isOnboarded === false && (
                                <Badge className="bg-yellow-500/10 text-yellow-600 dark:bg-yellow-400/10 dark:text-yellow-400">
                                  Pending Onboarding
                                </Badge>
                              )}
                            </div>
                            {company.description && (
                              <p className="text-sm text-muted-foreground truncate max-w-xs">
                                {company.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Employees Column */}
                      <TableCell>
                        <span className="text-foreground">
                          {company.employeeCount || '-'}
                        </span>
                      </TableCell>

                      {/* Revenue Column */}
                      <TableCell>
                        <span className="text-foreground">
                          {company.revenueRange || '-'}
                        </span>
                      </TableCell>

                      {/* Users Column */}
                      <TableCell>
                        <span className="text-foreground">
                          {company._count.users}
                        </span>
                      </TableCell>

                      {/* AI Stage Column */}
                      <TableCell>
                        {company.aiAdoptionStage ? (
                          <Badge className={getAiStageBadgeColor(company.aiAdoptionStage)}>
                            {company.aiAdoptionStage}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>

                      {/* Actions Column */}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/companies/${company.id}`)
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleEditClick(company, e)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteClick(company, e)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Card View - Mobile */}
            <div className="md:hidden space-y-4">
              {companies.map((company) => (
                <div
                  key={company.id}
                  className="bg-card rounded-lg shadow border border-border p-4 cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/companies/${company.id}`)}
                >
                  {/* Company Header */}
                  <div className="flex items-start space-x-3 mb-4">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={company.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">
                          {company.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-foreground truncate">
                          {company.name}
                        </h3>
                        {/* Demo Badge */}
                        {company.isDemo && (
                          <Badge className="bg-purple-500/10 text-purple-600 dark:bg-purple-400/10 dark:text-purple-400">
                            DEMO
                          </Badge>
                        )}
                        {/* Onboarding Status Badge */}
                        {company.isOnboarded ? (
                          <Badge className="bg-green-500/10 text-green-600 dark:bg-green-400/10 dark:text-green-400">
                            Onboarding Complete
                          </Badge>
                        ) : company.isOnboarded === false && (
                          <Badge className="bg-yellow-500/10 text-yellow-600 dark:bg-yellow-400/10 dark:text-yellow-400">
                            Pending Onboarding
                          </Badge>
                        )}
                      </div>
                      {company.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {company.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Company Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {/* Employees */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Employees</p>
                      <p className="text-sm font-medium text-foreground">
                        {company.employeeCount || '-'}
                      </p>
                    </div>

                    {/* Revenue */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Revenue</p>
                      <p className="text-sm font-medium text-foreground">
                        {company.revenueRange || '-'}
                      </p>
                    </div>

                    {/* Users */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Users</p>
                      <p className="text-sm font-medium text-foreground">
                        {company._count.users}
                      </p>
                    </div>

                    {/* AI Stage */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">AI Stage</p>
                      {company.aiAdoptionStage ? (
                        <Badge className={getAiStageBadgeColor(company.aiAdoptionStage)}>
                          {company.aiAdoptionStage}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/companies/${company.id}`)
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => handleEditClick(company, e)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-destructive hover:text-destructive"
                      onClick={(e) => handleDeleteClick(company, e)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Results Count */}
            <div className="bg-muted px-6 py-3 border-t border-border">
              <p className="text-sm text-foreground">
                Showing {companies.length} {companies.length === 1 ? 'company' : 'companies'}
              </p>
            </div>
          </>
        )}

        {/* Create Company Modal */}
        <CompanyFormModal
          open={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
          mode="create"
          onSuccess={handleCompanyCreated}
        />

        {/* Edit Company Modal */}
        {selectedCompany && (
          <CompanyFormModal
            open={isEditModalOpen}
            onOpenChange={setIsEditModalOpen}
            mode="edit"
            company={selectedCompany}
            onSuccess={handleCompanyUpdated}
          />
        )}

        {/* Delete Company Modal */}
        {companyToDelete && (
          <DeleteCompanyModal
            open={isDeleteModalOpen}
            onOpenChange={setIsDeleteModalOpen}
            company={companyToDelete}
            userCount={companyToDelete._count.users}
            onConfirm={handleDeleteConfirm}
          />
        )}
      </div>
    </div>
  )
}
